import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Fuse from 'fuse.js';
import React, { useMemo, useState } from 'react';

import type { LogEntry } from '@/types/browserAutomation';

interface LogsTableProps {
  logs: LogEntry[];
}

// Prettify JSON in log messages
const prettifyMessage = (message: string): React.ReactNode => {
  // Try to find JSON objects in the message
  const jsonPattern = /\{[\s\S]*?\}/;
  const match = message.match(jsonPattern);

  if (!match) {
    return <span className="whitespace-pre-wrap break-words">{message}</span>;
  }

  try {
    const jsonStr = match[0];
    const parsed = JSON.parse(jsonStr);
    const beforeJson = message.substring(0, match.index);
    const afterJson = message.substring((match.index || 0) + jsonStr.length);

    return (
      <>
        {beforeJson && (
          <span className="whitespace-pre-wrap break-words">{beforeJson}</span>
        )}
        <details className="inline">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-700 font-mono text-xs">
            {'{...}'}
          </summary>
          <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        </details>
        {afterJson && (
          <span className="whitespace-pre-wrap break-words">{afterJson}</span>
        )}
      </>
    );
  } catch {
    return <span className="whitespace-pre-wrap break-words">{message}</span>;
  }
};

const LogRow = ({ log, index }: { log: LogEntry; index: number }) => {
  // Extract context from message
  const contextMatch = log.message.match(/^\[([^\]]+)\]/);
  const context = contextMatch ? contextMatch[1] : null;
  const cleanMessage = log.message.replace(/^\[[^\]]+\]\s*/, '');

  const getLevelColor = (level?: string | null) => {
    const colors = {
      error: 'text-red-700 bg-red-50',
      warn: 'text-yellow-700 bg-yellow-50',
      info: 'text-blue-700 bg-blue-50',
      debug: 'text-gray-600 bg-gray-50',
    };
    return colors[level as keyof typeof colors] || 'text-gray-600 bg-gray-50';
  };

  return (
    <tr className={`border-b border-gray-100 ${getLevelColor(log.level)}`}>
      <td className="px-2 py-1.5 text-xs text-gray-400 font-mono w-12 text-right">
        {index + 1}
      </td>
      {context && (
        <td className="px-2 py-1.5 text-xs">
          <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-mono">
            {context}
          </span>
        </td>
      )}
      <td
        className={`px-2 py-1.5 text-xs text-gray-900 ${!context ? 'col-span-2' : ''}`}
        colSpan={!context ? 2 : 1}
      >
        {prettifyMessage(cleanMessage)}
      </td>
    </tr>
  );
};

export const LogsTable = ({ logs }: LogsTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [contextFilter, setContextFilter] = useState<string>('all');

  // Extract unique contexts
  const contexts = useMemo(() => {
    const contextSet = new Set<string>();
    logs.forEach((log) => {
      const match = log.message.match(/^\[([^\]]+)\]/);
      if (match) contextSet.add(match[1]);
    });
    return Array.from(contextSet).sort();
  }, [logs]);

  // Filter by context first
  const contextFilteredLogs = useMemo(() => {
    if (contextFilter === 'all') return logs;
    return logs.filter((log) => log.message.includes(`[${contextFilter}]`));
  }, [logs, contextFilter]);

  // Fuse search configuration
  const fuse = useMemo(
    () =>
      new Fuse(contextFilteredLogs, {
        keys: ['message', 'level'],
        threshold: 0.3,
        includeScore: true,
      }),
    [contextFilteredLogs],
  );

  // Apply search
  const displayedLogs = useMemo(() => {
    if (!searchQuery.trim()) return contextFilteredLogs;
    return fuse.search(searchQuery).map((result) => result.item);
  }, [contextFilteredLogs, fuse, searchQuery]);

  if (logs.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-8">
        No logs available
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search and filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        {contexts.length > 0 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setContextFilter('all')}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                contextFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {contexts.map((ctx) => (
              <button
                key={ctx}
                type="button"
                onClick={() => setContextFilter(ctx)}
                className={`text-xs px-2 py-1 rounded font-mono transition-colors ${
                  contextFilter === ctx
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                }`}
              >
                {ctx}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-xs text-gray-500">
        Showing {displayedLogs.length} of {logs.length} logs
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700 w-12">
                #
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                Context
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                Message
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {displayedLogs.map((log, index) => (
              <LogRow key={index} log={log} index={index} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
